import type { Container } from 'inversify'
import type { CaptureHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/captureHelper'
import type { OrthogonalHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/OrthogonalHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { CaptureType, ICaptureConfig } from '@/plugins/cad/module/core/auxiliaryTool/Capturer'
import { StartMode } from '@/plugins/cad/module/core/common/interface'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import { getLoggerManager } from '@/shared/logging'
import { DrawToolType, EditToolType, type IDrawToolParams, type IEditToolParams } from './interface'

const log = getLoggerManager().getLogger('cad')

/**
 * CAD 工具调度器（需传入 Inversify 容器实例）
 *
 * 宿主应从 CadHandle 或 CadPlugin 调用工具方法，
 * 此类仅作为内部 / 兼容入口保留。
 */
export class CADToolDispatcher {
  constructor(private readonly container: Container) {}

  /**
   * 获取绘制工具实例。
   *
   * @param toolType - 绘制工具类型
   * @returns 对应的工具实例；未注册时返回 null
   */
  getDrawTool<T extends BaseTool>(toolType: DrawToolType): T | null {
    const symbolMap: Partial<Record<DrawToolType, symbol>> = {
      [DrawToolType.Polyline]: TYPES.DrawPolylineTool,
      [DrawToolType.Arc]: TYPES.DrawArcTool,
      [DrawToolType.Circle]: TYPES.DrawCircleTool,
      [DrawToolType.Rect]: TYPES.DrawRectTool,
      [DrawToolType.Spline]: TYPES.DrawSplineTool,
      [DrawToolType.Text]: TYPES.DrawTextTool,
      [DrawToolType.Dimension]: TYPES.DrawDimensionTool,
      [DrawToolType.Polygon]: TYPES.DrawPolygonTool,
      [DrawToolType.Ellipse]: TYPES.DrawEllipseTool,
      [DrawToolType.Ray]: TYPES.DrawRayTool,
      [DrawToolType.Hatch]: TYPES.DrawHatchTool,
      [DrawToolType.MLeader]: TYPES.DrawMLeaderTool,
      [DrawToolType.AngularDimension]: TYPES.DrawAngularDimensionTool,
    }
    const sym = symbolMap[toolType]
    if (!sym) {
      log.warn(`不支持的绘制工具类型: "${toolType}"`)
      return null
    }
    return this.container.get<BaseTool>(sym) as T
  }

  /**
   * 调用绘制工具。
   *
   * @param params - 绘制参数，含工具类型、实体数据与可选控制器
   */
  draw(params: IDrawToolParams) {
    const {
      toolType,
      entityData,
      controller = this.container.get<CADController>(TYPES.CADController),
    } = params
    const drawTool = this.getDrawTool<BaseTool>(toolType)
    drawTool?.start(controller, StartMode.alone, entityData, (params as { mode?: unknown }).mode)
  }

  /**
   * 退出绘制工具。
   *
   * @param toolType - 绘制工具类型
   */
  exitDraw(toolType: DrawToolType) {
    const drawTool = this.getDrawTool<BaseTool>(toolType)
    drawTool?.exit()
  }

  /**
   * 获取编辑工具实例。
   *
   * @param toolType - 编辑工具类型
   * @returns 对应的工具实例；未注册时返回 null
   */
  getEditTool<T extends BaseTool>(toolType: EditToolType): T | null {
    const symbolMap: Partial<Record<EditToolType, symbol>> = {
      [EditToolType.Join]: TYPES.JoinEntityTool,
      [EditToolType.Copy]: TYPES.CopyEntityTool,
      [EditToolType.Move]: TYPES.MoveEntityTool,
      [EditToolType.Close]: TYPES.CloseEntityTool,
      [EditToolType.Rotate]: TYPES.RotateEntityTool,
      [EditToolType.Offset]: TYPES.OffsetEntityTool,
      [EditToolType.Break]: TYPES.BreakEntityTool,
      [EditToolType.Trim]: TYPES.TrimEntityTool,
      [EditToolType.Extend]: TYPES.ExtendEntityTool,
    }
    const sym = symbolMap[toolType]
    if (!sym) {
      log.warn(`不支持的编辑工具类型: "${toolType}"`)
      return null
    }
    return this.container.get<BaseTool>(sym) as T
  }

  /**
   * 调用编辑工具。
   *
   * @param params - 编辑参数，含工具类型与可选控制器
   */
  edit(params: IEditToolParams) {
    const { toolType, controller = this.container.get<CADController>(TYPES.CADController) } = params
    const editTool = this.getEditTool<BaseTool>(toolType)
    editTool?.start(controller, StartMode.alone)
  }

  /**
   * 退出编辑工具。
   *
   * @param toolType - 编辑工具类型
   */
  exitEdit(toolType: EditToolType) {
    const editTool = this.getEditTool<BaseTool>(toolType)
    editTool?.exit()
  }

  /**
   * 设置正交的启用状态。
   *
   * @param enabled - 是否启用
   */
  setOrthogonalEnabled(enabled: boolean) {
    const orthogonalHelper = this.container.get<OrthogonalHelper>(TYPES.OrthogonalHelper)
    orthogonalHelper.setEnabled(enabled)
  }

  /**
   * 获取正交的启用状态。
   *
   * @returns 是否已启用
   */
  isOrthogonalEnabled(): boolean {
    const orthogonalHelper = this.container.get<OrthogonalHelper>(TYPES.OrthogonalHelper)
    return orthogonalHelper.isEnabled()
  }

  /**
   * 设置捕捉的启用状态。
   *
   * @param enabled - 是否启用
   */
  setCaptureEnabled(enabled: boolean) {
    const captureHelper = this.container.get<CaptureHelper>(TYPES.CaptureHelper)
    captureHelper.setEnabled(enabled)
  }

  /**
   * 获取捕捉的启用状态。
   *
   * @returns 是否已启用
   */
  isCaptureEnabled(): boolean {
    const captureHelper = this.container.get<CaptureHelper>(TYPES.CaptureHelper)
    return captureHelper.isEnabled()
  }

  /**
   * 设置特定捕捉类型的启用状态。
   *
   * @param type - 捕捉类型
   * @param enabled - 是否启用
   */
  setCaptureType(type: CaptureType, enabled: boolean) {
    const captureHelper = this.container.get<CaptureHelper>(TYPES.CaptureHelper)
    captureHelper.setCaptureType(type, enabled)
  }

  /**
   * 设置捕捉配置。
   *
   * @param config - 需要覆盖的捕捉配置片段
   */
  setCaptureConfig(config: Partial<ICaptureConfig>) {
    const captureHelper = this.container.get<CaptureHelper>(TYPES.CaptureHelper)
    captureHelper.setConfig(config)
  }
}
